import React from 'react';
import PropTypes from 'prop-types';
import MuiAccordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AddCircle from '@material-ui/icons/AddCircle';
import { Box, IconButton } from '@material-ui/core';

/** @type {{ cardExpandedId: string, setCardExpandedId: (string) => void }} */
const initialContextValue = { cardExpandedId: '', setCardExpandedId: () => {} };
export const CardCollapsedContext = React.createContext(initialContextValue);

const CardCollapsed = ({
  classes,
  cardId,
  title,
  hasCounter,
  badgeCount,
  onAddClicked,
  onMoreClicked,
  isExpandedByDefault,
  children,
}) => {
  //eslint-disable-next-line no-nested-ternary
  const cardCounter = badgeCount || badgeCount === 0 ? badgeCount : Array.isArray(children) ? children.length : null;

  const { cardExpandedId, setCardExpandedId } = React.useContext(CardCollapsedContext);

  const cardHolderRef = React.useRef();

  const onAccordionChange = (event, isExpanded) => {
    setCardExpandedId(isExpanded ? cardId : '');

    if (isExpanded) {
      setTimeout(() => {
        cardHolderRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
      }, 300);
    }
  };

  return (
    <MuiAccordion
      ref={cardHolderRef}
      className={classes.cardHolder}
      defaultExpanded={isExpandedByDefault}
      expanded={!!cardExpandedId && cardExpandedId === cardId}
      data-testid={`card-collapsed-${cardId}`}
      onChange={onAccordionChange}
    >
      <AccordionSummary
        classes={{ expanded: classes.summaryExpanded }}
        className={classes.summary}
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${cardId}-content`}
        id={`${cardId}-header`}
      >
        {hasCounter && !!cardCounter && (
          <Typography variant="body1" className={classes.counter} data-testid="card-counter">
            {cardCounter}
          </Typography>
        )}
        <Typography variant="h6" className={classes.heading}>
          {title}
        </Typography>
      </AccordionSummary>
      {(onAddClicked || onMoreClicked) && (
        <AccordionSummary classes={{ root: classes.secondHeaderHolder }}>
          <Box display="flex" alignItems="center" className={classes.secondHeader}>
            {onAddClicked && (
              <IconButton onClick={onAddClicked} data-testid="add-button">
                <AddCircle />
              </IconButton>
            )}
            {onMoreClicked && (
              <IconButton onClick={onMoreClicked} className={classes.moreButton} data-testid="more-button">
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>
      )}
      <AccordionDetails classes={{ root: classes.root }}>{children}</AccordionDetails>
    </MuiAccordion>
  );
};

CardCollapsed.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  cardId: PropTypes.string,
  title: PropTypes.string,
  hasCounter: PropTypes.bool,
  badgeCount: PropTypes.number,
  onAddClicked: PropTypes.func,
  onMoreClicked: PropTypes.func,
  isExpandedByDefault: PropTypes.bool,
};

CardCollapsed.defaultProps = {
  classes: {},
  hasCounter: false,
  onMoreClicked: null,
  isExpandedByDefault: false,
};

export default CardCollapsed;
